import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { Grupo, GrupoService, ProdutoService, Seguro } from '../../../slidein_api';
import { msgAPIError } from '../../helpers';
import { MRT_Localization_PT } from 'mantine-react-table/locales/pt';
import { Button, Group, Modal, Stack, Title, Tooltip } from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleX,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useAuth } from '../../../user-auth/SlideInAuthContext';
import {
  CheckPermission,
  checkPermission,
  MASTER_ADMIN,
} from '../../../components/CheckPermission';
import { useParams } from 'react-router-dom';

type GrupoSegurosProps = {
  grupo: Grupo;
  refreshGrupo;
};

function getRows(table) {
  if (table && table.getSelectedRowModel() && table.getSelectedRowModel().rows) {
    return table.getSelectedRowModel().rows;
  } else {
    return [];
  }
}

export default function GrupoSeguros({ grupo, refreshGrupo }: GrupoSegurosProps) {
  const { colaborador } = useAuth();
  const { campanhaID } = useParams();
  const [seguros, setSeguros] = useState<Seguro[]>([]);
  const [addSegurosModalVisible, setAddSegurosModalVisible] = useState<boolean>(false);

  const fetchData = () => {
    if (checkPermission(MASTER_ADMIN, colaborador)) {
      ProdutoService.produtoSeguroList({ campanha: campanhaID }).then(
        (segurosall) => {
          setSeguros(segurosall);
        },
        (reason) => msgAPIError(reason)
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo<MRT_ColumnDef<Seguro>[]>(
    () => [
      { accessorKey: 'nome_interno', header: 'Nome Interno' },
      { accessorKey: 'titulo_cliente', header: 'Nome Cliente' },
      {
        accessorKey: 'activo',
        header: 'Ativo',
        Header: (
          <Tooltip label={'Activo, disponivel para compra'}>
            <div>Act.</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'valor',
        header: 'Valor',
        size: 100,
        Cell: (cell) => `${cell.cell.getValue()}€`,
      },
    ],
    []
  );
  const removeSeguros = () => {
    const all = grupo.seguros.map((gp) => gp.id);
    const remove = getRows(segurosTable).map((p) => p.id);
    GrupoService.grupoProdutosPartialUpdate({
      codigo: grupo.codigo,
      requestBody: {
        seguros: all.filter((a) => remove.findIndex((r) => r == a) == -1),
      },
    }).then(
      () => {
        fetchData();
        refreshGrupo();
        setAddSegurosModalVisible(false);
      },
      (reason) => msgAPIError(reason)
    );
  };
  const adicionarSeguros = () => {
    const segurosId = grupo.seguros.map((gp) => gp.id);
    segurosId.push(...getRows(allSegurosTable).map((p) => p.id));

    GrupoService.grupoProdutosPartialUpdate({
      codigo: grupo.codigo,
      requestBody: {
        seguros: segurosId,
      },
    }).then(
      () => {
        fetchData();
        refreshGrupo();
        setAddSegurosModalVisible(false);
      },
      (reason) => msgAPIError(reason)
    );
  };

  const segurosTable = useMantineReactTable({
    columns,
    data: grupo?.seguros || [],
    enableMultiRowSelection: checkPermission(MASTER_ADMIN, colaborador),
    enableSelectAll: checkPermission(MASTER_ADMIN, colaborador),
    enableRowSelection: checkPermission(MASTER_ADMIN, colaborador)
      ? (row) => row.original.nome_interno !== 'seguro_base'
      : false,
    selectAllMode: 'all',
    mantineTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
      },
      onClick: row.getToggleSelectedHandler(),
    }),
    getRowId: (originalRow) => originalRow.id.toString(),
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    positionToolbarAlertBanner: 'none',
    localization: MRT_Localization_PT,
    initialState: {
      density: 'xs',
    },
    renderTopToolbarCustomActions: () => (
      <Group>
        <CheckPermission
          roles={MASTER_ADMIN}
          goHome={false}>
          <Button
            leftIcon={<IconTrash />}
            variant={'outline'}
            color={'red'}
            disabled={getRows(segurosTable).length <= 0}
            onClick={() => {
              removeSeguros();
            }}>
            Remover
          </Button>
          <Button
            leftIcon={<IconPlus />}
            variant={'outline'}
            onClick={() => {
              setAddSegurosModalVisible(true);
            }}>
            Adicionar
          </Button>
        </CheckPermission>
      </Group>
    ),
  });

  const allSegurosTable = useMantineReactTable<Seguro>({
    columns,
    data: seguros || [],
    enableSelectAll: true,
    selectAllMode: 'all',
    getRowId: (originalRow) => originalRow.id.toString(),
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    positionToolbarAlertBanner: 'none',
    enableHiding: false,
    enablePagination: false,
    localization: MRT_Localization_PT,
    initialState: {
      density: 'xs',
    },
    enableRowSelection: (row) =>
      grupo.seguros.findIndex((gp) => gp.id == row.original.id) == -1 &&
      row.original.nome_interno !== 'seguro_base',
    mantineTableBodyRowProps: ({ row }) => ({
      sx: {
        opacity:
          grupo.seguros.findIndex((gp) => gp.id == row.original.id) == -1 &&
          row.original.nome_interno !== 'seguro_base'
            ? '100%'
            : '35%',
        cursor: 'pointer',
      },
      onClick: row.getToggleSelectedHandler(),
    }),
  });

  return (
    <Stack>
      <MantineReactTable table={segurosTable} />
      <Modal
        centered
        title={'Escolher Seguros:'}
        opened={addSegurosModalVisible}
        size={'auto'}
        onClose={() => {
          setAddSegurosModalVisible(false);
        }}>
        <Title order={4}>{getRows(allSegurosTable).length}&nbsp;Seguros Selecionados.</Title>
        <MantineReactTable table={allSegurosTable} />
        <Group
          position='right'
          mt='md'>
          <Button
            color={'yellow'}
            variant={'outline'}
            leftIcon={<IconX />}
            type='button'
            onClick={() => {
              setAddSegurosModalVisible(false);
            }}>
            Cancelar
          </Button>
          <Button
            variant={'outline'}
            disabled={getRows(allSegurosTable).length == 0}
            onClick={() => {
              adicionarSeguros();
              setAddSegurosModalVisible(false);
            }}
            leftIcon={<IconDeviceFloppy />}
            type='button'>
            Adicionar
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
